"use client";

import type { LenisRef } from "lenis/react";
import { ReactLenis } from "lenis/react";
import Image from "next/image";
import React from "react";
import { projects as allProjects } from "@/app/data/projects";

// Filter out work- prefixed items (join page items), same as WorkModal
const legacyProjects = allProjects.filter(
	(project) => !project.id.startsWith("work-"),
);

/**
 * Lightweight static grid fallback for mobile devices
 * Displays work images in a simple scrollable grid without Three.js overhead
 * Includes infinite scroll repeating effect like WorkModal
 */
export default function StaticWorkGrid() {
	const lenisRef = React.useRef<LenisRef | null>(null);
	const isDesktop = typeof window !== "undefined" && window.isDesktopView;

	// Infinite scroll: maintain a local state for grid items
	const [gridItems, setGridItems] = React.useState([...legacyProjects]);
	const [isFetching, setIsFetching] = React.useState(false);

	// Infinite scroll: append more items when near bottom
	React.useEffect(() => {
		const container = lenisRef.current?.wrapper as HTMLDivElement | null;
		if (!container) return;

		const handleScroll = () => {
			if (
				container.scrollHeight - container.scrollTop - container.clientHeight <
					1500 &&
				!isFetching
			) {
				setIsFetching(true);
				setTimeout(() => {
					setGridItems((prev) => [...prev, ...legacyProjects]);
					setIsFetching(false);
				}, 400); // simulate async fetch
			}
		};

		container.addEventListener("scroll", handleScroll);
		return () => container.removeEventListener("scroll", handleScroll);
	}, [isFetching]);

	return (
		<ReactLenis
			autoRaf
			ref={lenisRef}
			options={{
				orientation: "vertical",
				gestureOrientation: "vertical",
				smoothWheel: true,
				wheelMultiplier: 0.8,
				touchMultiplier: isDesktop ? 2 : 1,
				syncTouch: isDesktop,
				syncTouchLerp: 0.1,
				duration: 0.8,
				lerp: 0.18,
				easing: (t: number) => Math.min(1, 1.001 - 2 ** (-10 * t)),
			}}
			className="h-full w-full overflow-y-auto bg-white"
		>
			<div className="w-full p-4">
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
					{gridItems.map((project, idx) => {
						if (!project.image) return null;

						const [width, height] = project.aspectRatio
							? project.aspectRatio.split("/").map(Number)
							: [1920, 1380];

						return (
							<div
								key={`${project.id}-${idx}`}
								className="relative cursor-pointer overflow-hidden rounded-xl bg-neutral-100"
								style={{
									aspectRatio: `${width}/${height}`,
								}}
							>
								<Image
									src={project.image}
									alt={project.title}
									fill
									sizes="(max-width: 640px) 100vw, 50vw"
									className="object-cover"
									placeholder={project.blurDataURL ? "blur" : "empty"}
									blurDataURL={project.blurDataURL || undefined}
									loading={idx < 4 ? "eager" : "lazy"}
									priority={idx < 4}
								/>
							</div>
						);
					})}
				</div>
				{isFetching && (
					<div className="py-4 text-center text-gray-400">Loading more...</div>
				)}
			</div>
		</ReactLenis>
	);
}
